import React, { useState, useEffect, useRef } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import {
  Card, Table, Button, Space, Tag, Divider, Input, Select, Form,
  Typography, message, Popconfirm, Radio, Tooltip, Row, Col,
  Alert, Dropdown, Menu, Modal, Switch, List, Empty
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, ClockCircleOutlined,
  SearchOutlined, FilterOutlined, CloudUploadOutlined,
  EditOutlined, SettingOutlined,
  QuestionCircleOutlined, FileExcelOutlined, FileTextOutlined,
  InfoCircleOutlined, SyncOutlined, MessageOutlined, DownOutlined, SafetyCertificateOutlined,
  GlobalOutlined, CloudFilled, CloudOutlined, NodeIndexOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dnsService from '../../services/dnsService';
import GeoSelector from '../../../shared/components/GeoSelector';
import IspSelector from '../../../shared/components/IspSelector';
import CidrSelector from '../../../shared/components/CidrSelector';
import RecordBulkImportModal from '../../../shared/components/RecordBulkImportModal';
import QosManageModal from '../../components/dns/QosManageModal';
import DnsDnssec from './DnsDnssec';
import { adminPath } from '../../../shared/config/paths';
import AdminResourcePlanBar from '../../components/billing/AdminResourcePlanBar';

import '../../../styles/DnsRecords.css';

const { Title, Text } = Typography;
const { Option } = Select;
const EditableCell = ({
  editing, dataIndex, title, record, index, children, inputType, options, ...restProps
}) => {
  let inputNode;
  const isGeoEnabled = ['A', 'AAAA', 'CNAME'].includes(record?.type);

  if (dataIndex === 'route' && editing && isGeoEnabled) {
    // 调度线路列 - 渲染隐藏的表单字段以注册到表单
    inputNode = (
      <>
        <Form.Item name="geoData" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="ispData" hidden>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name="cidr_id" hidden>
          <input type="hidden" />
        </Form.Item>
      </>
    );
  } else if (inputType === 'select') {
    inputNode = (
      <Form.Item
        name={dataIndex}
        style={{ margin: 0 }}
        rules={[{ required: true, message: `请输入${title}!` }]}
      >
        <Select>
          {options.map(option => (
            <Option key={option.value} value={option.value}>{option.label}</Option>
          ))}
        </Select>
      </Form.Item>
    );
  } else if (inputType === 'geo') {
    inputNode = (
      <Form.Item
        name={dataIndex}
        style={{ margin: 0 }}
      >
        <GeoSelector />
      </Form.Item>
    );
  } else if (inputType === 'isp') {
    inputNode = (
      <Form.Item
        name={dataIndex}
        style={{ margin: 0 }}
      >
        <IspSelector />
      </Form.Item>
    );
  } else if (inputType === 'cidr') {
    inputNode = (
      <Form.Item
        name={dataIndex}
        style={{ margin: 0 }}
      >
        <CidrSelector />
      </Form.Item>
    );
  } else if (inputType === 'textarea') {
    inputNode = (
      <Form.Item
        name={dataIndex}
        style={{ margin: 0 }}
        rules={[{ required: true, message: `请输入${title}!` }]}
      >
        <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} />
      </Form.Item>
    );
  } else if (dataIndex === 'cdn_enabled') {
    const isCdnSupported = ['A', 'AAAA', 'CNAME'].includes(record?.type);
    inputNode = (
      <Form.Item
        name="cdn_enabled"
        valuePropName="checked"
        style={{ margin: 0 }}
      >
        <Switch
          checkedChildren="启用"
          unCheckedChildren="禁用"
          disabled={!isCdnSupported}
        />
      </Form.Item>
    );
  } else {
    inputNode = (
      <Form.Item
        name={dataIndex}
        style={{ margin: 0 }}
        rules={[{ required: true, message: `请输入${title}!` }]}
      >
        <Input />
      </Form.Item>
    );
  }

  // 对于 route 列，编辑时同时显示隐藏字段和原始内容（可点击打开Modal）
  if (dataIndex === 'route' && editing) {
    return (
      <td {...restProps}>
        {inputNode}
        {children}
      </td>
    );
  }

  return (
    <td {...restProps}>
      {editing ? inputNode : children}
    </td>
  );
};

const DnsRecords = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  // 监听窗口尺寸变化
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total) => `共 ${total} 条记录`
  });
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    geo: ''
  });
  const [editingKey, setEditingKey] = useState('');
  const [newRecord, setNewRecord] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { zoneId } = useParams();
  const location = useLocation();
  const [zoneName, setZoneName] = useState(location.state?.zoneName || '');
  const [zoneNameservers, setZoneNameservers] = useState([]);
  const [zoneNsGroups, setZoneNsGroups] = useState([]);
  const [isBulkImportVisible, setIsBulkImportVisible] = useState(false);
  const [isQosModalVisible, setIsQosModalVisible] = useState(false);

  // 调度线路配置 Modal 状态
  const [routeModalVisible, setRouteModalVisible] = useState(false);
  const [routeModalRecord, setRouteModalRecord] = useState(null);
  const [routeForm] = Form.useForm();
  const [qosPolicies, setQosPolicies] = useState([]);
  const [cidrUsage, setCidrUsage] = useState({});
  const [isVersionHistoryVisible, setIsVersionHistoryVisible] = useState(false);
  const [isSyncModalVisible, setIsSyncModalVisible] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [isDnssecVisible, setIsDnssecVisible] = useState(false);
  const searchInputRef = useRef(null);

  const isEditing = (record) => record.id === editingKey;

  // 判断是否为受保护的系统记录（根NS和SOA不可编辑/删除）
  const isProtectedRecord = (record) => {
    const type = (record.type || '').toUpperCase();
    const name = record.name || '';
    if (type === 'SOA') return true;
    if (type === 'NS' && (name === '@' || name === '')) return true;
    return false;
  };

  const edit = (record) => {
    // 准备表单数据
    const formData = {
      ...record,
      // 为地理位置选择器准备数据
      geoData: {
        continent: record.continent || '0',
        country: record.country || '0',
        province: record.province || '0',
        city: record.city || '0',
        continent_name: record.continent_name || '',
        country_name: record.country_name || '',
        province_name: record.province_name || '',
        city_name: record.city_name || ''
      },
      // 为ISP选择器准备数据
      ispData: {
        isp_country: record.isp_country || '',
        isp_type: record.isp_type || '',
        isp: record.isp || '0',
        isp_name: record.isp_name || ''
      }
    };

    form.setFieldsValue(formData);
    setEditingKey(record.id);
  };

  const cancel = () => {
    if (newRecord) {
      setRecords(records.filter(record => record.id !== 'new-record'));
      setNewRecord(null);
    }
    setEditingKey('');
  };

  // 打开调度线路配置Modal
  const openRouteModal = (record) => {
    // 优先从主表单读取当前编辑的值，否则用record的原始值
    const currentGeoData = form.getFieldValue('geoData') || {};
    const currentIspData = form.getFieldValue('ispData') || {};
    const currentCidrId = form.getFieldValue('cidr_id');

    // 处理 cidr_id，确保格式一致（对象格式）
    let cidrValue = currentCidrId !== undefined ? currentCidrId : (record?.cidr_id || 0);
    if (typeof cidrValue === 'number') {
      cidrValue = { cidr_id: cidrValue, cidr_name: record?.cidr_name || '' };
    }

    const formValues = {
      geoData: {
        continent: currentGeoData.continent || record?.continent || '0',
        country: currentGeoData.country || record?.country || '0',
        province: currentGeoData.province || record?.province || '0',
        city: currentGeoData.city || record?.city || '0',
        continent_name: currentGeoData.continent_name || record?.continent_name || '',
        country_name: currentGeoData.country_name || record?.country_name || '',
        province_name: currentGeoData.province_name || record?.province_name || '',
        city_name: currentGeoData.city_name || record?.city_name || ''
      },
      ispData: {
        isp_country: currentIspData.isp_country || record?.isp_country || '',
        isp_type: currentIspData.isp_type || record?.isp_type || '',
        isp: currentIspData.isp || record?.isp || '0',
        isp_name: currentIspData.isp_name || record?.isp_name || ''
      },
      cidr_id: cidrValue
    };

    setRouteModalRecord(record);
    // 先重置表单再设置值
    routeForm.resetFields();
    routeForm.setFieldsValue(formValues);
    setRouteModalVisible(true);
  };

  // 保存调度线路配置
  const handleRouteModalOk = () => {
    const values = routeForm.getFieldsValue();

    // 处理 cidr_id (可能是对象或数字)
    const cidrValue = values.cidr_id;
    const cidrId = typeof cidrValue === 'object' ? (cidrValue?.cidr_id || 0) : (cidrValue || 0);
    const cidrName = typeof cidrValue === 'object' ? (cidrValue?.cidr_name || '') : '';

    // 将选择的值同步到主表单
    form.setFieldsValue({
      geoData: values.geoData,
      ispData: values.ispData,
      cidr_id: cidrValue
    });

    // 同时更新当前编辑行的临时显示数据
    if (routeModalRecord) {
      const updatedRecords = records.map(r => {
        if (r.id === routeModalRecord.id) {
          return {
            ...r,
            continent: values.geoData?.continent || '0',
            country: values.geoData?.country || '0',
            province: values.geoData?.province || '0',
            city: values.geoData?.city || '0',
            continent_name: values.geoData?.continent_name || '',
            country_name: values.geoData?.country_name || '',
            province_name: values.geoData?.province_name || '',
            city_name: values.geoData?.city_name || '',
            isp: values.ispData?.isp || '0',
            isp_name: values.ispData?.isp_name || '',
            isp_country: values.ispData?.isp_country || '',
            isp_type: values.ispData?.isp_type || '',
            cidr_id: cidrId,
            cidr_name: cidrName
          };
        }
        return r;
      });
      setRecords(updatedRecords);
    }

    setRouteModalVisible(false);
    setRouteModalRecord(null);
  };

  const addNewRecord = () => {
    if (editingKey !== '') return;

    const newRecordData = {
      id: 'new-record',
      zone_id: zoneId,
      name: '',
      type: 'A',
      content: '',
      ttl: 3600,
      priority: 0,
      state: 'default',
      continent: '0',
      country: '0',
      province: '0',
      city: '0',
      isp: '0',
      cidr_id: 0,
      comment: ''
    };

    setRecords([newRecordData, ...records]);
    setNewRecord(newRecordData);
    form.setFieldsValue(newRecordData);
    setEditingKey('new-record');

    // 滚动到页面顶部以便编辑新记录
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();

      // 从表单中提取地理位置数据和ISP数据
      const { geoData, ispData, cidr_id: cidrValue, ...baseData } = row;

      // 处理 cidr_id (可能是对象或数字)
      const cidrId = typeof cidrValue === 'object' ? (cidrValue?.cidr_id || 0) : (cidrValue || 0);

      const updatedData = {
        ...baseData,
        // 扩展地理位置数据
        continent: geoData?.continent || '0',
        country: geoData?.country || '0',
        province: geoData?.province || '0',
        city: geoData?.city || '0',
        // 扩展ISP数据
        isp_country: ispData?.isp_country || '',
        isp_type: ispData?.isp_type || '',
        isp: ispData?.isp || '0',
        // CIDR ID
        cidr_id: cidrId
      };

      setLoading(true);

      const isNewRecord = key === 'new-record';
      let result;

      if (isNewRecord) {
        // 创建新记录
        result = await dnsService.createDnsRecord({
          ...updatedData,
          zone_id: zoneId
        });
      } else {
        // 更新现有记录
        result = await dnsService.updateDnsRecord({
          ...updatedData,
          id: key
        });
      }

      if (result.success) {
        // 如果有 CDN 相关消息，显示详细提示
        const successMsg = result.message || (isNewRecord ? '记录创建成功' : '记录更新成功');
        message.success(successMsg);
        setEditingKey('');
        setNewRecord(null);
        fetchRecords(); // 刷新记录列表
      } else {
        message.error(result.error || (isNewRecord ? '创建记录失败' : '更新记录失败'));
      }
    } catch (errInfo) {
      // 区分表单校验错误与接口错误：校验错误保留编辑态，接口错误提示原因
      if (errInfo && errInfo.errorFields) {
        console.log('Validate Failed:', errInfo);
      } else {
        message.error('保存失败: ' + (errInfo?.error || errInfo?.message || '未知错误'));
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (id) => {
    try {
      setLoading(true);
      const result = await dnsService.deleteDnsRecord(id);

      if (result.success) {
        message.success('记录删除成功');
        fetchRecords(); // 刷新记录列表
      } else {
        message.error(result.error || '删除记录失败');
      }
    } catch (error) {
      message.error('删除记录失败: ' + (error.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async (params = {}) => {
    const { pageSize, current, ...restParams } = params;

    setLoading(true);
    try {
      // 注：后端 getZone 仅返回区域本身（不含用户信息），故此处不再拉取所有者

      // 获取QoS策略
      const qosResult = await dnsService.getQosPolicies(zoneId);
      if (qosResult.success) {
        setQosPolicies(qosResult.data);
      }

      // 获取CIDR使用情况
      const cidrResult = await dnsService.getCidrUsage(zoneId);
      if (cidrResult.success) {
        setCidrUsage(cidrResult.data);
      }

      // 获取DNS节点
      const nodesResult = await dnsService.getDnsNodes();
      if (nodesResult.success) {
        setNodes(nodesResult.data);
      }

      // 获取DNS记录
      const result = await dnsService.getDnsRecords({
        zone_id: zoneId,
        page: params.current || pagination.current,
        limit: params.pageSize || pagination.pageSize,
        search: filters.search,
        type: filters.type,
        geo: filters.geo,
        ...restParams
      });

      if (result.success) {
        setRecords(result.data.records || []);
        setPagination({
          ...pagination,
          current: result.data.pagination?.current_page || result.data.page || 1,
          pageSize: result.data.pagination?.per_page || result.data.limit || 20,
          total: result.data.pagination?.total || result.data.total || 0
        });
      } else {
        message.error(result.error || '获取DNS记录失败');
      }
    } catch (error) {
      message.error('获取DNS记录失败: ' + (error.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [zoneId, filters]); // 当zoneId或filters变化时重新获取数据

  // 获取区域详情（域名 + 该域名实际使用的 NS 列表 / NS 池）
  useEffect(() => {
    const fetchZoneInfo = async () => {
      if (!zoneId) return;
      try {
        const result = await dnsService.getDnsZone(zoneId);
        // 后端 getZone 直接返回区域对象（data 即 zone），无嵌套 data.zone
        if (result.success && result.data) {
          // 优先使用 display_zone（IDN域名显示Unicode），否则使用 zone
          if (!zoneName && result.data.zone) {
            setZoneName(result.data.display_zone || result.data.zone);
          }
          // 该域名的 NS 主机名列表及其所属 NS 池（旧响应可能不含这些字段）
          setZoneNameservers(Array.isArray(result.data.nameservers) ? result.data.nameservers : []);
          setZoneNsGroups(Array.isArray(result.data.ns_groups) ? result.data.ns_groups : []);
        }
      } catch (error) {
        console.error('获取区域信息失败:', error);
      }
    };
    fetchZoneInfo();
  }, [zoneId]);

  const handleTableChange = (pagination, filters, sorter) => {
    // 处理排序
    let sortField = null;
    let sortOrder = null;
    if (sorter && sorter.field && sorter.order) {
      sortField = sorter.field;
      sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }

    fetchRecords({
      current: pagination.current,
      pageSize: pagination.pageSize,
      sortField,
      sortOrder
    });
  };

  const handleSearch = () => {
    const searchValue = searchInputRef.current.input.value;
    setFilters({
      ...filters,
      search: searchValue
    });
    setPagination({
      ...pagination,
      current: 1 // 搜索时重置为第一页
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value
    });
    setPagination({
      ...pagination,
      current: 1 // 筛选时重置为第一页
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: '',
      geo: ''
    });
    searchInputRef.current.input.value = '';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleBulkImportOk = async (records) => {
    setLoading(true);
    try {
      const result = await dnsService.bulkImportRecords({
        zone_id: zoneId,
        records
      });

      if (result.success) {
        message.success(`成功导入 ${result.data.success_count} 条记录`);
        if (result.data.failed_count > 0) {
          message.warning(`${result.data.failed_count} 条记录导入失败`);
        }
        fetchRecords();
      } else {
        message.error(result.error || '批量导入失败');
      }
    } catch (error) {
      message.error('批量导入失败: ' + (error.error || error.message));
    } finally {
      setLoading(false);
      setIsBulkImportVisible(false);
    }
  };

  const showQosModal = () => {
    setIsQosModalVisible(true);
  };

  const showVersionHistory = () => {
    setIsVersionHistoryVisible(true);
  };

  const showSyncModal = () => {
    setIsSyncModalVisible(true);
  };

  const handleSyncZone = async (nodeIds, syncType = 'all') => {
    if (!nodeIds || nodeIds.length === 0) {
      message.warning('请选择要同步的节点');
      return;
    }

    setSyncLoading(true);
    try {
      const result = await dnsService.syncToNodes({
        zone_id: zoneId,
        node_ids: nodeIds,
        type: syncType
      });

      if (result.success) {
        // 计算成功和失败的节点数量
        let successCount = 0;
        let failedCount = 0;

        for (const nodeId in result.data) {
          if (result.data[nodeId].success) {
            successCount++;
          } else {
            failedCount++;
          }
        }

        if (failedCount === 0) {
          message.success(`成功将区域同步到 ${successCount} 个节点`);
        } else {
          message.warning(`成功同步到 ${successCount} 个节点，${failedCount} 个节点同步失败`);
        }

        setIsSyncModalVisible(false);
      } else {
        message.error(result.error || '同步区域失败');
      }
    } catch (error) {
      message.error('同步区域失败: ' + (error.error || error.message));
    } finally {
      setSyncLoading(false);
    }
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 60,
      editable: true,
      sorter: true,
      ellipsis: true,
      render: (text) => {
        // 根记录 name 为 '@'，其 FQDN 即区域本身，不应拼成 "@.example.com"
        const fqdn = (text === '@' || !text) ? zoneName : `${text}.${zoneName}`;
        return <Tooltip title={fqdn}>{text}</Tooltip>;
      }
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      editable: true,
      sorter: true,
      render: (type) => <Tag color="blue">{type}</Tag>
    },
    {
      title: '内容',
      dataIndex: 'content',
      sorter: true,
      key: 'content',
      width: 150,
      editable: true,
      render: (text) => (
        <div style={{
          wordBreak: 'break-all',
          whiteSpace: 'pre-wrap',
          maxHeight: 80,
          overflow: 'auto',
          lineHeight: '1.4',
          fontSize: 13,
        }}>
          {text}
        </div>
      ),
    },
    {
      title: 'TTL',
      dataIndex: 'ttl',
      key: 'ttl',
      width: 70,
      editable: true,
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 70,
      editable: true,
      render: (priority, record) =>
        (record.type === 'MX' || record.type === 'SRV') ? priority : '-'
    },
    {
      title: '备注',
      dataIndex: 'comment',
      key: 'comment',
      width: 100,
      editable: true,
      ellipsis: true,
    },
    {
      title: '调度线路',
      dataIndex: 'route',
      key: 'route',
      width: 180,
      editable: true,
      render: (_, record) => {
        if (record.type !== 'A' && record.type !== 'AAAA' && record.type !== 'CNAME') {
          return <Text type="secondary">-</Text>;
        }

        // 构建显示文本
        const parts = [];
        if (record.city_name && record.city !== '0') {
          parts.push(record.city_name);
        } else if (record.province_name && record.province !== '0') {
          parts.push(record.province_name);
        } else if (record.country_name && record.country !== '0') {
          parts.push(record.country_name);
        } else if (record.continent_name && record.continent !== '0') {
          parts.push(record.continent_name);
        }
        if (record.isp_name && record.isp !== '0') {
          parts.push(record.isp_name);
        }
        if (record.cidr_name && record.cidr_id > 0) {
          parts.push(record.cidr_name);
        }

        const isEditing = editingKey === record.id;
        const displayText = parts.length > 0 ? parts.join(' | ') : '全局';

        // 构建tooltip内容
        const tooltipLines = [];
        if (record.continent_name && record.continent !== '0') tooltipLines.push(`大洲: ${record.continent_name}`);
        if (record.country_name && record.country !== '0') tooltipLines.push(`国家: ${record.country_name}`);
        if (record.province_name && record.province !== '0') tooltipLines.push(`省份: ${record.province_name}`);
        if (record.city_name && record.city !== '0') tooltipLines.push(`城市: ${record.city_name}`);
        if (record.isp_name && record.isp !== '0') tooltipLines.push(`ISP: ${record.isp_name}`);
        if (record.cidr_name && record.cidr_id > 0) tooltipLines.push(`线路: ${record.cidr_name}`);

        const tooltipContent = tooltipLines.length > 0 ? (
          <div>
            {tooltipLines.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
        ) : '全局默认';

        return (
          <Tooltip title={tooltipContent} placement="topLeft">
            <div
              style={{
                cursor: isEditing ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
              onClick={() => {
                if (isEditing) {
                  openRouteModal(record);
                }
              }}
            >
              <NodeIndexOutlined style={{ color: parts.length > 0 ? '#1890ff' : '#d9d9d9', fontSize: 14 }} />
              {record.country && record.country !== '0' && <CountryFlag code={record.country_name} style={{ marginInlineEnd: 0 }} />}
              <Text
                style={{
                  fontSize: 12,
                  color: isEditing ? '#1890ff' : undefined,
                  textDecoration: isEditing ? 'underline' : 'none',
                  maxWidth: 140,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayText}
              </Text>
              {isEditing && <EditOutlined style={{ color: '#1890ff', fontSize: 12 }} />}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: 'CDN',
      dataIndex: 'cdn_enabled',
      key: 'cdn_enabled',
      width: 50,
      align: 'center',
      render: (enabled, record) => {
        if (record.type !== 'A' && record.type !== 'AAAA' && record.type !== 'CNAME') {
          return <Text type="secondary">-</Text>;
        }

        const handleCdnToggle = () => {
          // 如果正在编辑此记录，直接切换表单值
          if (editingKey === record.id) {
            const currentValue = form.getFieldValue('cdn_enabled');
            form.setFieldsValue({ cdn_enabled: !currentValue });
            return;
          }

          // 非编辑模式，弹出确认框
          Modal.confirm({
            title: enabled ? '关闭CDN加速' : '开启CDN加速',
            content: enabled
              ? '确定要关闭此记录的CDN加速吗？同主机名下的其他记录也会同步关闭。'
              : '确定要开启此记录的CDN加速吗？同主机名下的其他记录也会同步开启。',
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
              try {
                const result = await dnsService.updateDnsRecord({
                  id: record.id,
                  cdn_enabled: !enabled
                });
                if (result.success) {
                  message.success(result.message || (enabled ? 'CDN已关闭' : 'CDN已开启'));
                  fetchRecords();
                } else {
                  message.error(result.error || '操作失败');
                }
              } catch (error) {
                message.error('操作失败: ' + (error.error || error.message));
              }
            }
          });
        };

        const CloudIcon = enabled ? CloudFilled : CloudOutlined;
        return (
          <Tooltip title={enabled ? 'CDN加速已启用（点击关闭）' : 'CDN加速未启用（点击开启）'}>
            <CloudIcon
              style={{
                fontSize: 18,
                color: enabled ? '#1890ff' : '#d9d9d9',
                cursor: 'pointer'
              }}
              onClick={handleCdnToggle}
            />
          </Tooltip>
        );
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => save(record.id)}
              style={{ paddingLeft: 0, paddingRight: 0 }}
            >
              保存
            </Button>
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={cancel}
              style={{ paddingLeft: 0, paddingRight: 0 }}
              danger
            >
              取消
            </Button>
          </Space>
        ) : (
          <Space>
            <Tooltip title={isProtectedRecord(record) ? '系统记录，不可修改' : ''}>
              <Button
                type="link"
                disabled={editingKey !== '' || isProtectedRecord(record)}
                onClick={() => edit(record)}
                icon={<EditOutlined />}
                style={{ paddingLeft: 0, paddingRight: 0 }}
              >
                编辑
              </Button>
            </Tooltip>
            {isProtectedRecord(record) ? (
              <Tooltip title="系统记录，不可删除">
                <Button
                  type="link"
                  disabled
                  danger
                  icon={<DeleteOutlined />}
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                >
                  删除
                </Button>
              </Tooltip>
            ) : (
              <Popconfirm
                title="确定要删除此记录吗？"
                onConfirm={() => deleteRecord(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  type="link"
                  disabled={editingKey !== ''}
                  danger
                  icon={<DeleteOutlined />}
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                >
                  删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // 移动端记录卡片组件
  const RecordCard = ({ record }) => {
    const editable = isEditing(record);
    const isGeoEnabled = ['A', 'AAAA', 'CNAME'].includes(record.type);

    // 构建地理位置显示
    const getGeoDisplay = () => {
      if (!isGeoEnabled) return null;
      const geoInfo = [];
      if (record.continent_name && record.continent !== '0') geoInfo.push(record.continent_name);
      if (record.country_name && record.country !== '0') geoInfo.push(record.country_name);
      if (record.province_name && record.province !== '0') geoInfo.push(record.province_name);
      if (record.city_name && record.city !== '0') geoInfo.push(record.city_name);
      return geoInfo.length > 0 ? geoInfo.join(' > ') : '全局';
    };

    if (editable) {
      // 编辑模式下的卡片
      return (
        <Card
          size="small"
          style={{ marginBottom: 12 }}
          className="dns-record-card editing"
        >
          <Form form={form} component={false}>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Form.Item name="name" label="名称" style={{ marginBottom: 8 }} rules={[{ required: true }]}>
                  <Input placeholder="记录名称" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="type" label="类型" style={{ marginBottom: 8 }} rules={[{ required: true }]}>
                  <Select>
                    <Option value="A">A</Option>
                    <Option value="AAAA">AAAA</Option>
                    <Option value="CNAME">CNAME</Option>
                    <Option value="MX">MX</Option>
                    <Option value="TXT">TXT</Option>
                    <Option value="NS">NS</Option>
                    <Option value="SRV">SRV</Option>
                    <Option value="CAA">CAA</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="content" label="内容" style={{ marginBottom: 8 }} rules={[{ required: true }]}>
                  <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="记录内容" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="ttl" label="TTL" style={{ marginBottom: 8 }}>
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="priority" label="优先级" style={{ marginBottom: 8 }}>
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="comment" label="备注" style={{ marginBottom: 8 }}>
                  <Input placeholder="备注" />
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ margin: '12px 0' }} />
            <Space>
              <Button type="primary" icon={<SaveOutlined />} onClick={() => save(record.id)} loading={loading}>
                保存
              </Button>
              <Button icon={<CloseOutlined />} onClick={cancel}>
                取消
              </Button>
            </Space>
          </Form>
        </Card>
      );
    }

    // 非编辑模式下的卡片
    return (
      <Card
        size="small"
        style={{ marginBottom: 12 }}
        className="dns-record-card"
        actions={[
          <Tooltip title={isProtectedRecord(record) ? '系统记录，不可修改' : ''} key="edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              disabled={editingKey !== '' || isProtectedRecord(record)}
              onClick={() => edit(record)}
            >
              编辑
            </Button>
          </Tooltip>,
          isProtectedRecord(record) ? (
            <Tooltip title="系统记录，不可删除" key="delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled
              >
              删除
            </Button>
          </Tooltip>
          ) : (
            <Popconfirm
              title="确定要删除此记录吗？"
              onConfirm={() => deleteRecord(record.id)}
              okText="确定"
              cancelText="取消"
              key="delete"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={editingKey !== ''}
              >
                删除
              </Button>
            </Popconfirm>
          )
        ]}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <Text strong style={{ fontSize: 15 }}>
              {record.name || '@'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>.{zoneName}</Text>
          </div>
          <Tag color="blue">{record.type}</Tag>
        </div>

        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>内容：</Text>
          <Text style={{ wordBreak: 'break-all' }}>{record.content}</Text>
        </div>

        <Row gutter={[8, 4]} style={{ fontSize: 12 }}>
          <Col span={12}>
            <Text type="secondary">TTL: </Text>
            <Text>{record.ttl}</Text>
          </Col>
          {(record.type === 'MX' || record.type === 'SRV') && (
            <Col span={12}>
              <Text type="secondary">优先级: </Text>
              <Text>{record.priority}</Text>
            </Col>
          )}
        </Row>

        {isGeoEnabled && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space size={4}>
              <NodeIndexOutlined style={{ color: getGeoDisplay() !== '全局' ? '#1890ff' : '#d9d9d9', fontSize: 14 }} />
              {record.country && record.country !== '0' && <CountryFlag code={record.country_name} style={{ marginInlineEnd: 0 }} />}
              <Text style={{ fontSize: 12, color: '#666' }}>
                {(() => {
                  const parts = [];
                  if (record.city_name && record.city !== '0') parts.push(record.city_name);
                  else if (record.province_name && record.province !== '0') parts.push(record.province_name);
                  else if (record.country_name && record.country !== '0') parts.push(record.country_name);
                  else if (record.continent_name && record.continent !== '0') parts.push(record.continent_name);
                  if (record.isp_name && record.isp !== '0') parts.push(record.isp_name);
                  if (record.cidr_name && record.cidr_id > 0) parts.push(record.cidr_name);
                  return parts.length > 0 ? parts.join(' | ') : '全局';
                })()}
              </Text>
            </Space>
            <Tooltip title={record.cdn_enabled ? 'CDN加速已启用' : 'CDN加速未启用'}>
              {record.cdn_enabled
                ? <CloudFilled style={{ fontSize: 18, color: '#1890ff' }} />
                : <CloudOutlined style={{ fontSize: 18, color: '#d9d9d9' }} />
              }
            </Tooltip>
          </div>
        )}

        {record.comment && (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>备注: {record.comment}</Text>
          </div>
        )}
      </Card>
    );
  };

  const mergedColumns = columns.map(col => {
    if (!col.editable) {
      return col;
    }

    let baseInputType = 'text';
    let options = [];

    if (col.dataIndex === 'type') {
      baseInputType = 'select';
      options = [
        { value: 'A', label: 'A (IPv4地址)' },
        { value: 'AAAA', label: 'AAAA (IPv6地址)' },
        { value: 'CNAME', label: 'CNAME (别名)' },
        { value: 'MX', label: 'MX (邮件服务器)' },
        { value: 'TXT', label: 'TXT (文本记录)' },
        { value: 'NS', label: 'NS (域名服务器)' },
        { value: 'SRV', label: 'SRV (服务记录)' },
        { value: 'CAA', label: 'CAA (证书授权)' },
      ];
    } else if (col.dataIndex === 'geo') {
      baseInputType = 'geo';
    }

    return {
      ...col,
      onCell: (record) => {
        // TXT 和 SPF 类型的 content 字段使用 textarea
        let inputType = baseInputType;
        if (col.dataIndex === 'content' && (record?.type === 'TXT' || record?.type === 'SPF')) {
          inputType = 'textarea';
        }
        return {
          record,
          inputType,
          dataIndex: col.dataIndex,
          title: col.title,
          options,
          editing: isEditing(record),
        };
      },
    };
  });

  // 将节点设置为菜单选项
  const nodeOptions = nodes.map(node => {
    return {
      label: (
        <span>
          {node.hostname}
          <span className="text-muted" style={{ marginLeft: 8 }}>
            {node.ip_address}
          </span>
          <Tag
            color={node.status === 'active' ? 'success' : 'default'}
            style={{ marginLeft: 8 }}
          >
            {node.status}
          </Tag>
        </span>
      ),
      key: node.id,
    };
  });

  return (
    <div>
      <AdminResourcePlanBar type="dns" id={zoneId} />
      <div style={{ marginBottom: 16 }}>
        <Row gutter={[8, 8]} align="middle" justify="space-between">
          <Col xs={24} sm={12}>
            <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>
              {isMobile ? (zoneName || '加载中...') : `区域记录: ${zoneName || '加载中...'}`}
            </Title>
          </Col>
          <Col xs={24} sm={12}>
            <Space wrap style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              {!isMobile && (
                <Button onClick={() => navigate(adminPath('/dns/zones'))}>
                  返回区域列表
                </Button>
              )}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={addNewRecord}
                disabled={editingKey !== ''}
              >
                {isMobile ? '添加' : '添加记录'}
              </Button>
              <Dropdown
                overlay={
                  <Menu>
                    {isMobile && (
                      <Menu.Item key="0" onClick={() => navigate(adminPath('/dns/zones'))}>
                        返回区域列表
                      </Menu.Item>
                    )}
                    {isMobile && <Menu.Divider />}
                    <Menu.Item key="1" icon={<FileExcelOutlined />} onClick={() => setIsBulkImportVisible(true)}>
                      批量导入
                    </Menu.Item>
                    <Menu.Item key="3" icon={<InfoCircleOutlined />} onClick={showQosModal}>
                      QoS设置
                    </Menu.Item>
                    <Menu.Item key="4" icon={<SyncOutlined />} onClick={showSyncModal}>
                      同步到节点
                    </Menu.Item>
                    <Menu.Item key="5" icon={<ClockCircleOutlined />} onClick={showVersionHistory}>
                      版本历史
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item key="6" icon={<SafetyCertificateOutlined />} onClick={() => setIsDnssecVisible(true)}>
                      DNSSEC 设置
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button icon={<SettingOutlined />}>
                  {isMobile ? '' : '更多操作'} <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>
      </div>

      {(Array.isArray(zoneNameservers) && zoneNameservers.length > 0) ||
      (Array.isArray(zoneNsGroups) && zoneNsGroups.length > 0) ? (
        <Card
          size="small"
          title={<span><NodeIndexOutlined /> 该域名的名称服务器 (NS)</span>}
          style={{ marginBottom: 16 }}
          bordered={false}
        >
          {Array.isArray(zoneNameservers) && zoneNameservers.length > 0 && (
            <div style={{ marginBottom: Array.isArray(zoneNsGroups) && zoneNsGroups.length > 0 ? 12 : 0 }}>
              <Space wrap>
                {zoneNameservers.map((ns) => (
                  <Tag color="blue" key={ns}>{ns}</Tag>
                ))}
              </Space>
            </div>
          )}
          {Array.isArray(zoneNsGroups) && zoneNsGroups.length > 0 && (
            <Table
              size="small"
              rowKey={(r) => r.child_group_id || r.ns_hostname}
              dataSource={zoneNsGroups}
              pagination={false}
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'NS 主机名', dataIndex: 'ns_hostname', key: 'ns_hostname' },
                {
                  title: 'NS 池',
                  dataIndex: 'pool_name',
                  key: 'pool_name',
                  render: (name, r) => name || (r.pool_id ? `#${r.pool_id}` : '-'),
                },
                {
                  title: '状态',
                  dataIndex: 'status',
                  key: 'status',
                  render: (s) => <Tag color={s === 'active' ? 'green' : 'default'}>{s || '-'}</Tag>,
                },
                {
                  title: '活跃节点数',
                  dataIndex: 'active_nodes',
                  key: 'active_nodes',
                  render: (n) => {
                    const count = Number(n) || 0;
                    return <Text style={{ color: count === 0 ? '#cf1322' : undefined }}>{count}</Text>;
                  },
                },
              ]}
            />
          )}
        </Card>
      ) : null}

      <Card className="card-with-search-wrapper" bordered={false}>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={8}>
              <Input
                placeholder={isMobile ? "搜索..." : "搜索记录名称、内容或注释"}
                prefix={<SearchOutlined />}
                ref={searchInputRef}
                onKeyPress={handleKeyPress}
                style={{ width: '100%' }}
                allowClear
              />
            </Col>
            <Col xs={12} sm={4}>
              <Select
                placeholder="类型"
                style={{ width: '100%' }}
                onChange={(value) => handleFilterChange('type', value)}
                value={filters.type || undefined}
                allowClear
              >
                <Option value="">所有</Option>
                <Option value="A">A</Option>
                <Option value="AAAA">AAAA</Option>
                <Option value="CNAME">CNAME</Option>
                <Option value="MX">MX</Option>
                <Option value="TXT">TXT</Option>
                <Option value="NS">NS</Option>
                <Option value="SRV">SRV</Option>
                <Option value="CAA">CAA</Option>
              </Select>
            </Col>
            <Col xs={12} sm={4}>
              <Select
                placeholder="位置"
                style={{ width: '100%' }}
                onChange={(value) => handleFilterChange('geo', value)}
                value={filters.geo || undefined}
                allowClear
              >
                <Option value="">所有</Option>
                <Option value="geo">地理定位</Option>
                <Option value="default">全局默认</Option>
              </Select>
            </Col>
            <Col xs={24} sm={8}>
              <Space wrap>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  {isMobile ? '' : '搜索'}
                </Button>
                <Button icon={<FilterOutlined />} onClick={clearFilters}>
                  {isMobile ? '' : '清除'}
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {isMobile ? (
          /* 移动端卡片视图 */
          <div>
            <List
              dataSource={records}
              loading={loading}
              renderItem={(record) => (
                <RecordCard key={record.id} record={record} />
              )}
              locale={{ emptyText: '暂无记录' }}
            />
            {/* 移动端分页 */}
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Space direction="vertical" size={8}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {pagination.total > 0 ? `共 ${pagination.total} 条记录` : ''}
                </Text>
                <Space>
                  <Button
                    size="small"
                    disabled={pagination.current <= 1}
                    onClick={() => handleTableChange({ current: pagination.current - 1, pageSize: pagination.pageSize }, {}, {})}
                  >
                    上一页
                  </Button>
                  <Text>{pagination.current} / {Math.ceil(pagination.total / pagination.pageSize) || 1}</Text>
                  <Button
                    size="small"
                    disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                    onClick={() => handleTableChange({ current: pagination.current + 1, pageSize: pagination.pageSize }, {}, {})}
                  >
                    下一页
                  </Button>
                </Space>
              </Space>
            </div>
          </div>
        ) : (
          /* 桌面端表格视图 */
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              bordered
              dataSource={records}
              columns={mergedColumns}
              rowKey="id"
              pagination={pagination}
              loading={loading}
              onChange={handleTableChange}
              scroll={{ x: 1000 }}
              locale={{
                emptyText: <Empty description="暂无DNS记录，点击上方添加记录" />
              }}
            />
          </Form>
        )}
      </Card>

      <RecordBulkImportModal
        visible={isBulkImportVisible}
        onCancel={() => setIsBulkImportVisible(false)}
        onOk={handleBulkImportOk}
        zoneName={zoneName}
      />

      <QosManageModal
        visible={isQosModalVisible}
        onCancel={() => setIsQosModalVisible(false)}
        zoneId={zoneId}
        zoneName={zoneName}
      />

      {/* 调度线路配置 Modal */}
      <Modal
        title="调度线路配置"
        open={routeModalVisible}
        onOk={handleRouteModalOk}
        onCancel={() => {
          setRouteModalVisible(false);
          setRouteModalRecord(null);
        }}
        width={500}
      >
        <Form form={routeForm} layout="vertical">
          <Form.Item name="geoData" label="地理位置">
            <GeoSelector />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.geoData?.country_code !== currentValues.geoData?.country_code
            }
          >
            {({ getFieldValue }) => {
              const geoData = getFieldValue('geoData') || {};
              return (
                <Form.Item name="ispData" label="ISP运营商">
                  <IspSelector geoCountry={geoData.country_code || ''} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item name="cidr_id" label="自定义线路">
            <CidrSelector />
          </Form.Item>
        </Form>
      </Modal>

      <DnsDnssec
        visible={isDnssecVisible}
        onClose={() => setIsDnssecVisible(false)}
        zoneId={zoneId}
        zoneName={zoneName}
      />
    </div>
  );
};

export default DnsRecords;
